import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, Mail, Lock, ArrowLeft, Key, Check } from "lucide-react";
import axios from "axios";
import { LOGIN } from "@/constants/testIds";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Ceva nu a funcționat. Vă rugăm să încercați din nou.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Forgot password flow
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState("");

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      await login(email, password);
      navigate("/admin");
    } catch (err) {
      console.error(err);
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message || "Eroare la autentificare.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setError("");
    setForgotSuccess("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`,
        { email: forgotEmail }
      );
      setForgotSuccess(response.data.message || "Un link de resetare a fost generat în consola serverului.");
    } catch (err) {
      console.error(err);
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Eroare la trimiterea e-mailului.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div 
      data-testid="login-container"
      className="min-h-screen bg-[#0A0A0A] flex flex-col justify-center items-center px-4 py-12 relative overflow-hidden"
    >
      {/* Decorative backdrop */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full filter blur-3xl -z-10" />

      {/* Back to Home Link */}
      <Link
        to="/"
        data-testid="login-back-to-home-link"
        className="absolute top-8 left-8 flex items-center space-x-2 text-sm text-white/60 hover:text-[#D4AF37] transition-colors"
      >
        <ArrowLeft size={16} />
        <span>Înapoi pe site</span>
      </Link>

      <div className="w-full max-w-md bg-[#121212] border border-[#D4AF37]/20 p-8 sm:p-10 relative">
        <div className="absolute top-0 left-0 w-full h-[4px] bg-[#D4AF37]" />

        {/* Header */}
        <div className="text-center mb-8 space-y-2">
          <div className="mx-auto w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center text-[#D4AF37] border border-[#D4AF37]/20 mb-3">
            <Shield size={24} />
          </div>
          <h2 className="font-serif-lux text-2xl sm:text-3xl text-white font-medium">
            {showForgot ? "Resetare Parolă" : "Autentificare Admin"}
          </h2>
          <p className="text-xs text-white/50 tracking-wider uppercase">
            Elysium Events Mansion
          </p>
        </div>

        {/* Global Error Banner */}
        {error && (
          <div 
            data-testid="login-error-alert"
            className="mb-6 p-4 bg-red-950/40 border border-red-900/30 text-red-400 text-sm font-light"
          >
            {error}
          </div>
        )}

        {/* Toggleable forms */}
        {!showForgot ? (
          /* Login Form */
          <form onSubmit={handleLoginSubmit} className="space-y-6">
            <div className="space-y-2">
              <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Adresă E-mail</label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 text-white/30" size={16} />
                <input
                  type="email"
                  required
                  data-testid={LOGIN.emailInput}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@elysiumevents.ro"
                  className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Parolă</label>
                <button
                  type="button"
                  data-testid={LOGIN.forgotPasswordLink}
                  onClick={() => {
                    setShowForgot(true);
                    setError("");
                  }}
                  className="text-xs text-[#D4AF37] hover:underline"
                >
                  Ai uitat parola?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 text-white/30" size={16} />
                <input
                  type="password"
                  required
                  data-testid={LOGIN.passwordInput}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid={LOGIN.submitButton}
              className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-4 transition-all duration-300 flex items-center justify-center space-x-2"
            >
              {loading ? (
                <span className="animate-pulse">Se autentifică...</span>
              ) : (
                <span>Autentificare</span>
              )}
            </button>
          </form>
        ) : (
          /* Forgot Password Form */
          <div className="space-y-6">
            {forgotSuccess ? (
              <div 
                data-testid="forgot-password-success"
                className="space-y-4 text-center py-4"
              >
                <div className="mx-auto w-10 h-10 bg-green-950/40 text-green-400 flex items-center justify-center rounded-full border border-green-900/30">
                  <Check size={20} />
                </div>
                <p className="text-sm text-white/80">{forgotSuccess}</p>
                <button
                  onClick={() => {
                    setShowForgot(false);
                    setForgotSuccess("");
                    setError("");
                  }}
                  className="text-xs text-[#D4AF37] hover:underline uppercase tracking-wider font-semibold"
                >
                  Înapoi la autentificare
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotSubmit} className="space-y-6">
                <p className="text-sm text-white/60 leading-relaxed font-light">
                  Introduceți adresa de e-mail asociată contului de admin. Vă vom genera un token securizat de resetare în logurile serverului.
                </p>

                <div className="space-y-2">
                  <label className="text-xs tracking-widest uppercase text-white/70 font-semibold">Adresă E-mail</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3.5 text-white/30" size={16} />
                    <input
                      type="email"
                      required
                      data-testid="forgot-email-input"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      placeholder="admin@elysiumevents.ro"
                      className="w-full bg-[#0A0A0A] border border-[#D4AF37]/20 focus:border-[#D4AF37] text-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-1 focus:ring-[#D4AF37] transition-colors"
                    />
                  </div>
                </div>

                <div className="flex flex-col space-y-3">
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    data-testid="forgot-submit-button"
                    className="w-full bg-[#D4AF37] hover:bg-[#C5A880] text-black text-xs font-semibold tracking-widest uppercase py-4 transition-all duration-300"
                  >
                    {forgotLoading ? "Se trimite..." : "Resetează Parola"}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setShowForgot(false);
                      setError("");
                    }}
                    className="text-xs text-white/50 hover:text-white transition-colors py-2 text-center"
                  >
                    Renunță
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
